import React, { ReactNode, useEffect, useState } from 'react';
import { ApolloError, useReactiveVar } from '@apollo/client';

import config from '@occc/config';
import { useGetChains } from '@occc-app/dataAccessLayer/chain/queries';
import { accessTokenVar } from '@occc-app/Auth/authReactiveVariables';

import { Chain } from './types';
import { getChainIdFromLocalStorage, getCurrentChain, saveChainIdToLocalStorage } from './utils';

type ChainProviderProps = {
  children: ReactNode;
};

type ChainContextValue = {
  loading: boolean;
  error: ApolloError | undefined;
  chain: Chain | null;
  changeChain: (chain: Chain) => void;
};

export const ChainContext = React.createContext<ChainContextValue | undefined>(undefined);

export const ChainProvider = ({ children }: ChainProviderProps) => {
  const [chain, setChain] = useState<Chain | null>(null);
  const accessToken = useReactiveVar(accessTokenVar);

  // chains are implemented in the Activate API
  // we shouldn't use chains in the Lite version of the CC as
  // Activate API doesn't work for such environments
  const shouldSkip = !accessToken || config.isLiteVersion;

  const {
    data: chains,
    loading,
    error,
  } = useGetChains(
    {},
    {
      skip: shouldSkip,
    },
  );

  const changeChain = React.useCallback((newChain: Chain) => {
    setChain(newChain);
    saveChainIdToLocalStorage(newChain.id);
  }, []);

  const contextValue = React.useMemo(() => {
    return {
      loading,
      error,
      chain,
      changeChain,
    };
  }, [loading, error, chain, changeChain]);

  useEffect(() => {
    if (!chains) {
      return;
    }

    const currentChainId = getChainIdFromLocalStorage();
    const currentChain = getCurrentChain(currentChainId, chains);

    if (!currentChain) {
      return;
    }

    setChain(currentChain);

    if (currentChainId !== currentChain.id) {
      saveChainIdToLocalStorage(currentChain.id);
    }
  }, [chains]);

  return <ChainContext.Provider value={contextValue}>{children}</ChainContext.Provider>;
};
